import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, catchError, map, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';

export type RingStrength = 'soft' | 'normal' | 'hard' | 'mega';

export interface RingOptions {
  light?: boolean;
}

@Injectable({ providedIn: 'root' })
export class RingService {

  public constructor(
    private readonly http: HttpClient,
    private readonly snackBar: MatSnackBar
  ) { }

  public ring(times: number, strength: RingStrength, opts?: RingOptions): Observable<void> {
    const lambdaUrl: string | undefined = environment.AwsIot?.lambdaUrl;
    const topic: string | undefined = environment.AwsIot?.topicCmd;

    if (typeof lambdaUrl !== 'string' || lambdaUrl.length === 0) {
      const msg = 'Missing Lambda URL in environment.AwsIot.lambdaUrl.';
      this.showErrorToast(msg);
      return throwError(() => new Error(msg));
    }

    if (typeof topic !== 'string' || topic.length === 0) {
      const msg = 'Missing topic in environment.AwsIot.topicCmd.';
      this.showErrorToast(msg);
      return throwError(() => new Error(msg));
    }

    const body = {
      topic: topic,
      payload: {
        times: this.sanitizeTimes(times),
        forcePct: this.mapStrengthToForce(strength),
        light: opts?.light === true
      }
    };

    const headers = new HttpHeaders({ 'content-type': 'application/json' });

    return this.http.post<{ ok: boolean }>(lambdaUrl, body, { headers }).pipe(
      map(res => {
        if (!res || res.ok !== true) {
          throw new Error('Bell command failed.');
        }
      }),
      catchError((err: unknown) => {
        const msg = this.describeHttpError(err);
        this.showErrorToast(msg);
        return throwError(() => new Error(msg));
      })
    );
  }

  public ringSoft(times: number = 1, opts?: RingOptions): Observable<void> {
    return this.ring(times, 'soft', opts);
  }

  public ringNormal(times: number = 1, opts?: RingOptions): Observable<void> {
    return this.ring(times, 'normal', opts);
  }

  public ringHard(times: number = 1, opts?: RingOptions): Observable<void> {
    return this.ring(times, 'hard', opts);
  }

  public ringMega(times: number = 1, opts?: RingOptions): Observable<void> {
    return this.ring(times, 'mega', opts);
  }

  private sanitizeTimes(n: number): number {
    if (!Number.isFinite(n)) {
      return 1;
    }
    if (n < 1) {
      return 1;
    }
    if (n > 10) {
      return 10;
    }
    return Math.floor(n);
  }

  private mapStrengthToForce(s: RingStrength): number {
    if (s === 'soft') {
      return 40;
    }
    if (s === 'normal') {
      return 70;
    }
    if (s === 'hard') {
      return 85;
    }
    return 100;
  }

  private describeHttpError(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      const textBody = typeof err.error === 'string' ? err.error : undefined;
      const msgFromJson = !textBody ? (err.error?.error || err.message) : textBody;
      if (msgFromJson && msgFromJson.length > 0) {
        return msgFromJson;
      }
      return `HTTP ${err.status}`;
    }
    if (typeof err === 'string') {
      return err;
    }
    const unknownErr = err as { message?: string };
    return unknownErr?.message ?? 'Unknown error.';
  }

  private showErrorToast(message: string): void {
    this.snackBar.open(message, 'Dismiss', {
      duration: 5000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: ['snackbar-error']
    });
  }
}
